import { useActiveUserContext, useAppConfigContext, useAssignedAppTypeContext } from '@vivli/core/infrastructure/context';
import { AssignedAppTypeEnum, PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { IStudy } from '@vivli/features/studies/infrastructure/interface';
import { StudyStatusEnum } from '@vivli/features/studies/infrastructure/enum';

export function useAdminStudyPermissions(study: IStudy, dataPackageSubmitted: boolean) {
    const { user: activeUser, hasPermission, orgHasPermission, hasAnyPermission } = useActiveUserContext();
    const { isAmr } = useAssignedAppTypeContext();
    const config = useAppConfigContext();
    //TODO PERMFE - redo with permissions - below change all isVivAdmin to userIsVivliAdmin+
    const userIsVivliAdmin = () => {
        return hasPermission(PermissionsEnum.EditStudyAdv);
    };
    const isVivAdmin = userIsVivliAdmin();

    const studyIpdIsDownloadable = (study: IStudy) => {
        const hasDataPackageId = study?.ipdDataPackageId !== null;
        return hasDataPackageId && study?.downloadableStudyIPDDataPackage;
    };

    const ipdReadyToDownload = () => {
        return dataPackageSubmitted && studyIpdIsDownloadable(study);
    };

    const userIsInStudyOrg = () => {
        return activeUser?.orgMemberships.some((o) => o.orgId === study.orgId);
    };

    const userIsOrgAdmin = () => {
        //TODO PERMFE - redo with permissions+
        let isOrgAdmin = false;
        const testOrgAdmin = activeUser?.orgMemberships.some((o) => orgHasPermission(study.orgId, PermissionsEnum.ProviderActAdv));
        if (!!testOrgAdmin) {
            isOrgAdmin = testOrgAdmin;
        }
        return isOrgAdmin;
    };
    //TODO PERMFE - redo with permissions (and all below)+
    const userIsOrgDataProvider = () => {
        return activeUser?.orgMemberships.some((o) => orgHasPermission(study.orgId, PermissionsEnum.DataContrib));
    };
    //TODO PERMFE - redo with permissions+
    const userIsDataProviderOrgAdmin = () => {
        return activeUser?.orgMemberships.some((o) => orgHasPermission(study.orgId, PermissionsEnum.ContribChat));
    };

    //TODO PERMFE - redo with permissions+
    const userIsDatasetOwner = () => {
        return activeUser?.orgMemberships.some(
            (o) =>
                orgHasPermission(study.orgId, PermissionsEnum.DataContrib) &&
                orgHasPermission(study.orgId, PermissionsEnum.OwnsSubmission) &&
                study.ownerIds.some((i) => i === activeUser.userId)
        );
    };

    const userCanRemovePosting = () => {
        return userIsVivliAdmin() && study.status === StudyStatusEnum.Posted;
    };

    const userCanDownloadIpd = () => {
        const activeUserHasDownloadPermissions = isAmr
            ? true //anyone can download, even a Vivli Admin
            : !isVivAdmin;

        return activeUserHasDownloadPermissions && dataPackageSubmitted;
    };

    const userCanUploadIpd = () => {
        const activeUserHasUploadPermissions = isAmr
            ? true //anyone can download, even a Vivli Admin
            : !isVivAdmin;

        return activeUserHasUploadPermissions && !dataPackageSubmitted;
    };

    const userCanSeeDataPackage = () => {
        const canAlwaysSee = isAmr
            ? true //anyone can upload or download, even a Vivli Admin
            : userIsVivliAdmin();

        return canAlwaysSee || userIsOrgDataProvider();
    };

    const canSeeDataCount = () => {
        return config.irusCountOn && study.status === StudyStatusEnum.Posted;
    };

    const userCanApprove = () => {
        switch (study?.status) {
            case StudyStatusEnum.SubmittedToVivliForPreCheck:
            case StudyStatusEnum.CurationBlocked:
            case StudyStatusEnum.Posted:
                return isVivAdmin;
            case StudyStatusEnum.AwaitingCuration:
            case StudyStatusEnum.CurationInProgress:
            case StudyStatusEnum.CurationInternalReview:
            case StudyStatusEnum.CurationRevisionsRequired:
                return isVivAdmin || hasAnyPermission([PermissionsEnum.CurateStudy]);
            case StudyStatusEnum.AwaitingCurationQaReview:
            case StudyStatusEnum.CurationQaReviewInProgress:
                return isVivAdmin || hasAnyPermission([PermissionsEnum.QaCuration]); //TODO PERMFE - redo with permissions+
            case StudyStatusEnum.Draft:
            case StudyStatusEnum.SubmittedToVivliForPostCheck:
                // case StudyStatus.SponsorApprovesCuration:
                // case StudyStatus.SponsorRequiresCurationRevisions:
                //TODO PERMFE - redo with permissions+
                return orgHasPermission(study?.orgId, PermissionsEnum.DataContrib) || isVivAdmin; //TODO PERMFE - redo with permissions+
        }
        return false;
    };

    //note that userCanSave permissions don't necessarily
    //mean the user can save every field. Except for Vivli admins,
    //saving after Draft stage is limited to a few fields, so
    //although the user may see the Save button, it will only
    //work for the permitted fields.
    const userCanSave = () => {
        if (!study) {
            return false;
        }
        const isStudyOwner = userIsDataProviderOrgAdmin() || userIsDatasetOwner();
        switch (study?.status) {
            case StudyStatusEnum.Draft:
            case StudyStatusEnum.SubmittedToVivliForPostCheck:
                return (
                    //TODO PERMFE - redo with permissions+
                    orgHasPermission(study?.orgId, PermissionsEnum.DataContrib) || isVivAdmin || isStudyOwner
                );
            case StudyStatusEnum.AwaitingCuration:
            case StudyStatusEnum.CurationInProgress:
            case StudyStatusEnum.CurationInternalReview:
            case StudyStatusEnum.CurationRevisionsRequired:
                return (
                    //TODO PERMFE - redo with permissions+
                    orgHasPermission(study?.annotatorOrgId, PermissionsEnum.CurateStudy) || isVivAdmin || isStudyOwner
                );
            case StudyStatusEnum.AwaitingCurationQaReview:
            case StudyStatusEnum.CurationQaReviewInProgress:
                return (
                    //TODO PERMFE - redo with permissions
                    orgHasPermission(study?.qaReviewerOrgId, PermissionsEnum.QaCuration) || isVivAdmin || isStudyOwner
                );
            case StudyStatusEnum.SubmittedToVivliForPreCheck:
            case StudyStatusEnum.Posted:
                return isVivAdmin || isStudyOwner;
        }
        return false;
    };

    const userCanDelete = () => {
        switch (study?.status) {
            case StudyStatusEnum.Draft:
            case StudyStatusEnum.SubmittedToVivliForPreCheck:
                return (
                    //TODO PERMFE - redo with permissions+
                    orgHasPermission(study?.orgId, PermissionsEnum.DataContrib) || isVivAdmin
                );
            case StudyStatusEnum.SubmittedToVivliForPostCheck:
            case StudyStatusEnum.SponsorApprovesCuration:
            case StudyStatusEnum.SponsorRequiresCurationRevisions:
                return isVivAdmin;
        }
        return false;
    };

    const userCanDeleteStudyIpdPackage = () => {
        return isVivAdmin && study.ipdDataPackageId !== null;
    };

    const userCanSoftDeleteStudyIpdPackage = () => {
        //TODO PERMFE - redo with permissions+
        return orgHasPermission(study?.orgId, PermissionsEnum.DataContrib) || isVivAdmin;
    };

    return {
        userCanDownloadIpd,
        userIsOrgAdmin,
        userIsOrgDataProvider,
        userIsInStudyOrg,
        userIsVivliAdmin,
        userCanRemovePosting,
        userCanApprove,
        userCanSave,
        userCanDelete,
        userCanDeleteStudyIpdPackage,
        userCanSoftDeleteStudyIpdPackage,
        studyIpdIsDownloadable,
        ipdReadyToDownload,
        userCanSeeDataPackage,
        canSeeDataCount,
        userCanUploadIpd,
    };
}
